import type { OtpPaymentClaimStatus, OtpPaymentClaimView, OtpPlatformOtpTotalsView } from '@erp/shared';
import { ArrowLeft, HandCoins, Inbox } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button as ShButton,
  Skeleton,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { formatDateTime } from '../../lib/intl.js';
import { platformApi } from '../../lib/platformApi.js';

import { errCode, fmtUsd } from './_shared/OtpShared.js';
import { KpiCard } from '../reports/_shared/KpiCard.js';

type StatusFilter = OtpPaymentClaimStatus;

const STATUS_VARIANT: Record<OtpPaymentClaimStatus, 'warning' | 'success' | 'danger' | 'secondary'> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'danger',
  expired: 'secondary',
};

/**
 * Cross-client inbox for "I paid X" balance top-up requests customers submit
 * from their own dashboard (otp.vylo-tech.com). Confirming one credits the
 * balance for real — it inserts into otp_service_payments, exactly like the
 * "Record payment" box on a client's own detail page does; this is simply the
 * second entry point into that same table.
 */
export function PlatformOtpPaymentClaimsPage(): JSX.Element {
  const { t, i18n } = useTranslation('platform');
  const { t: tc } = useTranslation();
  const [items, setItems] = useState<OtpPaymentClaimView[] | null>(null);
  const [totals, setTotals] = useState<OtpPlatformOtpTotalsView | null>(null);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<OtpPaymentClaimView | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [claimsR, totalsR] = await Promise.all([
        platformApi().get<{ items: OtpPaymentClaimView[] }>('/otp-service/payment-claims', { params: { status } }),
        platformApi().get<OtpPlatformOtpTotalsView>('/otp-service/totals'),
      ]);
      setItems(claimsR.data.items);
      setTotals(totalsR.data);
    } catch {
      setItems([]);
      toast.error(t('otpService.paymentClaims.loadError', { defaultValue: 'Could not load top-up requests.' }));
    }
  }, [status, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const statusLabel = useMemo(
    () => ({
      pending: t('otpService.paymentClaims.statusPending', { defaultValue: 'Pending' }),
      confirmed: t('otpService.paymentClaims.statusConfirmed', { defaultValue: 'Confirmed' }),
      rejected: t('otpService.paymentClaims.statusRejected', { defaultValue: 'Rejected' }),
      expired: t('otpService.paymentClaims.statusExpired', { defaultValue: 'Expired' }),
    }),
    [t],
  );

  const onConfirm = async (claim: OtpPaymentClaimView): Promise<void> => {
    const ok = await confirmDialog({
      title: t('otpService.paymentClaims.confirmTitle', { defaultValue: 'Confirm this top-up?' }),
      message: t('otpService.paymentClaims.confirmMessage', {
        defaultValue: "This credits {{amount}} to {{client}}'s balance immediately.",
        amount: fmtUsd(claim.amount),
        client: claim.clientName ?? '',
      }),
      confirmLabel: t('otpService.paymentClaims.confirm', { defaultValue: 'Confirm' }),
    });
    if (!ok) return;
    setBusyId(claim.id);
    try {
      await platformApi().post(`/otp-service/payment-claims/${claim.id}/confirm`);
      toast.success(t('otpService.paymentClaims.confirmed', { defaultValue: 'Confirmed — the balance was credited.' }));
      await load();
    } catch (err) {
      // 409 here means another admin tab already confirmed/rejected this exact
      // claim between page-load and this click — refresh so the stale row (and
      // its now-wrong Confirm/Reject buttons) disappears instead of retrying.
      toast.error(
        errCode(err) === 'otp_service.payment_claim.already_resolved'
          ? t('otpService.paymentClaims.alreadyResolved', { defaultValue: 'Already resolved by someone else — refreshing.' })
          : t('otpService.paymentClaims.confirmError', { defaultValue: 'Could not confirm this request.' }),
      );
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (claim: OtpPaymentClaimView): void => {
    setRejectReason('');
    setRejectTarget(claim);
  };

  const onReject = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!rejectTarget) return;
    const note = rejectReason.trim();
    if (!note) {
      toast.error(t('otpService.paymentClaims.rejectReasonRequired', { defaultValue: 'Enter a reason before rejecting.' }));
      return;
    }
    setRejecting(true);
    try {
      await platformApi().post(`/otp-service/payment-claims/${rejectTarget.id}/reject`, { resolutionNote: note });
      toast.success(t('otpService.paymentClaims.rejected', { defaultValue: 'Rejected.' }));
      setRejectTarget(null);
      await load();
    } catch (err) {
      toast.error(
        errCode(err) === 'otp_service.payment_claim.already_resolved'
          ? t('otpService.paymentClaims.alreadyResolved', { defaultValue: 'Already resolved by someone else — refreshing.' })
          : t('otpService.paymentClaims.rejectError', { defaultValue: 'Could not reject this request.' }),
      );
      setRejectTarget(null);
      await load();
    } finally {
      setRejecting(false);
    }
  };

  const columns: DataTableColumn<OtpPaymentClaimView>[] = [
    {
      id: 'client',
      header: t('otpService.paymentClaims.table.client', { defaultValue: 'Client' }),
      sortValue: (row) => row.clientName ?? '',
      cellClassName: 'font-medium',
      cell: (row) =>
        row.clientId ? (
          <Link to={`/platform/otp-service/${row.clientId}`} className="hover:text-brand hover:underline underline-offset-2">
            {row.clientName}
          </Link>
        ) : (
          row.clientName
        ),
    },
    {
      id: 'amount',
      header: t('otpService.paymentClaims.table.amount', { defaultValue: 'Amount' }),
      align: 'end',
      sortValue: (row) => Number(row.amount),
      cell: (row) => <span dir="ltr" className="font-mono tabular-nums">{fmtUsd(row.amount)}</span>,
    },
    {
      id: 'note',
      header: t('otpService.paymentClaims.table.note', { defaultValue: 'Note' }),
      cellClassName: 'max-w-[18rem] truncate text-fg-muted',
      cell: (row) => row.note || '—',
    },
    {
      id: 'requested',
      header: t('otpService.paymentClaims.table.requested', { defaultValue: 'Requested' }),
      sortValue: (row) => row.createdAt,
      cellClassName: 'text-xs text-fg-muted',
      cell: (row) => formatDateTime(row.createdAt, { locale: i18n.language }),
    },
    {
      id: 'status',
      header: t('otpService.paymentClaims.table.status', { defaultValue: 'Status' }),
      sortValue: (row) => row.status,
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <Badge variant={STATUS_VARIANT[row.status]}>{statusLabel[row.status]}</Badge>
          {row.status === 'rejected' && row.resolutionNote && (
            <span className="text-xs text-fg-muted">{row.resolutionNote}</span>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: t('otpService.paymentClaims.table.actions', { defaultValue: 'Actions' }),
      align: 'end',
      cell: (row) =>
        row.status === 'pending' ? (
          <div className="flex justify-end gap-2">
            <ShButton size="sm" variant="outline" disabled={busyId === row.id} onClick={() => void onConfirm(row)}>
              {t('otpService.paymentClaims.confirm', { defaultValue: 'Confirm' })}
            </ShButton>
            <ShButton size="sm" variant="ghost" onClick={() => openReject(row)}>
              {t('otpService.paymentClaims.reject', { defaultValue: 'Reject' })}
            </ShButton>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-warning/15 text-warning">
            <Inbox className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">
              {t('otpService.paymentClaims.title', { defaultValue: 'Balance top-up requests' })}
            </h1>
            <p className="text-sm text-fg-muted">
              {t('otpService.paymentClaims.subtitle', {
                defaultValue: 'Customers who told us they paid — confirm to credit their balance, or reject with a reason.',
              })}
            </p>
          </div>
        </div>
        <Link to="/platform/otp-service">
          <ShButton variant="outline">
            <ArrowLeft className="h-4 w-4" />
            {t('otpService.paymentClaims.backToClients', { defaultValue: 'Back to clients' })}
          </ShButton>
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          tone="success"
          icon={HandCoins}
          label={t('otpService.kpi.totalAccepted', { defaultValue: 'Total accepted' })}
          value={totals ? fmtUsd(totals.totalAccepted) : '—'}
          loading={!totals}
        />
        <KpiCard
          tone="warning"
          icon={Inbox}
          label={t('otpService.kpi.pendingTopups', { defaultValue: 'Pending top-ups' })}
          value={totals ? String(totals.pendingClaimsCount) : '—'}
          loading={!totals}
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="h-10 rounded-md border border-border bg-bg px-3 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            aria-label={t('otpService.paymentClaims.statusFilter', { defaultValue: 'Status' })}
          >
            <option value="pending">{t('otpService.paymentClaims.statusPending', { defaultValue: 'Pending' })}</option>
            <option value="confirmed">{t('otpService.paymentClaims.statusConfirmed', { defaultValue: 'Confirmed' })}</option>
            <option value="rejected">{t('otpService.paymentClaims.statusRejected', { defaultValue: 'Rejected' })}</option>
            <option value="expired">{t('otpService.paymentClaims.statusExpired', { defaultValue: 'Expired' })}</option>
          </select>
        </div>
      </Card>

      {items === null ? (
        <Card className="p-6"><Skeleton className="h-48 w-full" /></Card>
      ) : items.length === 0 ? (
        <EmptyState
          title={
            status === 'pending'
              ? t('otpService.paymentClaims.emptyPending', { defaultValue: 'No pending top-up requests.' })
              : t('otpService.paymentClaims.empty', { defaultValue: 'Nothing here.' })
          }
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(row) => row.id} paginated={false} searchable={false} />
        </Card>
      )}

      {/* Reject needs a reason (shown to the customer) — the only action here that
          takes free text, so it gets its own small dialog rather than confirmDialog. */}
      <Dialog open={rejectTarget !== null} onOpenChange={(o) => { if (!rejecting && !o) setRejectTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('otpService.paymentClaims.rejectTitle', { defaultValue: 'Reject this top-up' })}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => void onReject(e)} className="flex flex-col gap-3" noValidate>
            <div>
              <label htmlFor="claim-reject-reason" className="mb-1.5 block text-sm font-medium text-fg">
                {t('otpService.paymentClaims.rejectReasonLabel', { defaultValue: 'Reason (shown to the customer)' })}
              </label>
              <textarea
                id="claim-reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
                placeholder={t('otpService.paymentClaims.rejectReasonPlaceholder', {
                  defaultValue: 'e.g. no matching transfer found',
                })}
              />
            </div>
            <DialogFooter>
              <ShButton type="button" variant="outline" onClick={() => setRejectTarget(null)} disabled={rejecting}>
                {tc('actions.cancel', { defaultValue: 'Cancel' })}
              </ShButton>
              <Button type="submit" variant="danger" loading={rejecting}>
                {t('otpService.paymentClaims.rejectSubmit', { defaultValue: 'Reject request' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
